import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Grid2 as Grid,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material'
import { EditTemplateProvider } from './EditTemplateContext'
import TemplateDetail from './TemplateDetail'
import TemplateComponents from './TemplateComponents'
import { useEditTemplate } from './EditTemplateContext'
import { useParams, useNavigate } from 'react-router-dom'

const EditTemplateContent: React.FC = () => {
  const navigate = useNavigate()
  const {
    isLoading,
    alteredState,
    isValidTemplate,
    loadTemplateError,
    errorValue,
    resetAlteredState,
    saveContext,
    isSaving,
    saveError,
    templateId,
    isCopyMode,
  } = useEditTemplate()
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null)
  const [snackbarOpen, setSnackbarOpen] = useState(false)

  const isEditMode = !!templateId && !isCopyMode
  const pageTitle = isEditMode ? 'Edit Template' : isCopyMode ? 'Copy Template' : 'Create Template'
  const saveButtonText = isEditMode ? 'Save' : 'Create'

  useEffect(() => {
    if (saveError) {
      setSnackbarOpen(true)
    }
  }, [saveError])

  // Handle navigation with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (alteredState) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [alteredState])

  const handleNavigation = (navigationFn: () => void) => {
    if (alteredState) {
      setPendingNavigation(() => navigationFn)
      setShowConfirmDialog(true)
    } else {
      navigationFn()
    }
  }

  const confirmNavigation = () => {
    if (pendingNavigation) {
      pendingNavigation()
    }
    setShowConfirmDialog(false)
    setPendingNavigation(null)
  }

  const cancelNavigation = () => {
    setShowConfirmDialog(false)
    setPendingNavigation(null)
  }

  if (loadTemplateError) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h4" color="error" sx={{ mb: 2 }}>
          Error Loading Template
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {errorValue?.message || 'An unknown error occurred while loading the template.'}
        </Typography>
        <Button variant="contained" onClick={() => navigate('/admin/templates')}>
          Back to Templates
        </Button>
      </Box>
    )
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
          p: 2,
        }}
      >
        <CircularProgress size={40} />
        <Typography sx={{ mt: 2 }}>Loading template...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2, minWidth: '1050px', maxWidth: '1100px' }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {pageTitle}
      </Typography>
      <Grid container spacing={3}>
        {/* Section 1: Template Details */}
        <Grid size={{ xs: 12 }}>
          <TemplateDetail />
        </Grid>
        {/* Section 2: Template Components */}
        <Grid size={{ xs: 12 }}>
          <TemplateComponents />
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          color="warning"
          sx={{ mr: 2 }}
          onClick={() =>
            handleNavigation(() => {
              resetAlteredState()
              navigate(-1)
            })
          }
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!alteredState || !isValidTemplate || isSaving}
          onClick={saveContext}
        >
          {isSaving ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
          {saveButtonText}
        </Button>
      </Box>

      {/* Error Snackbar */}
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity="error" sx={{ width: '100%' }}>
          {saveError || 'An error occurred while saving.'}
        </Alert>
      </Snackbar>

      {/* Navigation Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={cancelNavigation}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>You have unsaved changes. Are you sure you want to leave? Your changes will be lost.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelNavigation} color="primary">
            Stay
          </Button>
          <Button onClick={confirmNavigation} color="warning" variant="contained">
            Leave Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

const EditTemplateV2: React.FC = () => {
  // Extract the 'id' parameter from the URL
  const { templateId } = useParams()

  return (
    <EditTemplateProvider templateId={templateId || null}>
      <EditTemplateContent />
    </EditTemplateProvider>
  )
}

export default EditTemplateV2
